import _ from 'lodash';
import { getCurrentInstance, computed } from 'vue-demi';
import { IFlexibleLayoutComponents } from '@onereach/grid-layout';

import { Layout } from '@/types';
import { useUILayoutSettings } from '@/modules/settings-store';
import { useRouter } from '@/composables';
import {
  BASIC_COMPONENTS,
  COMPONENT_CATEGORY_KEYS,
  CARD_COMPONENT_NAME,
} from '@/constants';

export const useLayoutSwitcher = () => {
  const vm = getCurrentInstance()!;
  const { query, AppRouter } = useRouter();
  const {
    cardComponentsSet,
    layout,
    layoutWorkspaceList,
    updateLayoutSettings,
    saveUILayoutSettings,
  } = useUILayoutSettings();

  const workspacesById = computed(() => _.keyBy(layoutWorkspaceList.value, 'key'));
  const defaultWorkspacesById = computed(() => {
    return _.keyBy(layoutWorkspaceList.value.map(w => w.original!), 'key');
  });

  /** Default workspace according to settings */
  const defaultWorkspace = computed(() => {
    return layoutWorkspaceList.value.find(w => w.isActive)
      || layoutWorkspaceList.value[0];
  });

  const activeLayoutQueryId = computed(() => {
    return query.value[AppRouter.QUERY_PARAMS.LAYOUT] as string;
  });
  const activeWorkspaceQueryId = computed(() => {
    return query.value[AppRouter.QUERY_PARAMS.WORKSPACE] as string;
  });

  /* These values should always exist for safety */
  const activeWorkspaceId = computed(() => {
    return activeWorkspaceQueryId.value || defaultWorkspace.value.key;
  });
  const activeLayoutId = computed(() => {
    return activeLayoutQueryId.value || defaultWorkspace.value.defaultLayout;
  });
  const activeWorkspace = computed(() => {
    return workspacesById.value[activeWorkspaceId.value];
  });

  const workspaceComponentsByKey = computed(() => {
    const components = {} as IFlexibleLayoutComponents;

    for (const key of activeWorkspace.value!.components) {
      const basic = BASIC_COMPONENTS.find(c => c.key == key);
      if (basic) {
        components[key] = {
          ...basic,
          label: vm.proxy.$te(basic.label)
            ? (vm.proxy.$t(basic.label) as string)
            : basic.label,
        };
      }

      const card = cardComponentsSet.value[key];
      if (card) {
        components[key] = {
          key: card.id,
          component: CARD_COMPONENT_NAME,
          label: card.label,
          category: COMPONENT_CATEGORY_KEYS.CARD,
        };
      }
    }

    return components;
  });

  /**
   * Determine starting workspace and layout based on query params.
   * If there are any mismatches between IDs - use default values.
   */
  const initializeActiveWorkspace = () => {
    /* Use default settings when there are no query params */
    if (!activeWorkspaceQueryId.value && !activeLayoutQueryId.value) {
      return changeWorkspaceOrLayout({ workspaceId: defaultWorkspace.value.key });
    }

    /* Check if workspace exists */
    if (activeWorkspaceQueryId.value && !activeLayoutQueryId.value) {
      const wActive = workspacesById.value[activeWorkspaceQueryId.value]
        || defaultWorkspace.value;
      return changeWorkspaceOrLayout({ workspaceId: wActive.key });
    }

    if (activeWorkspaceQueryId.value && activeLayoutQueryId.value) {
      const wActive = workspacesById.value[activeWorkspaceQueryId.value]
        || defaultWorkspace.value;
      const layout = wActive.layouts[activeLayoutQueryId.value]
        || wActive.layouts[wActive.defaultLayout];

      return changeWorkspaceOrLayout({
        workspaceId: wActive.key,
        layoutId: layout.key,
      });
    }

    /* Check if both workspace and layout exist or use default values */
    if (!activeWorkspaceQueryId.value && activeLayoutQueryId.value) {
      const workspace = getWorkspaceByLayoutId(activeLayoutQueryId.value);
      workspace
        ? changeWorkspaceOrLayout({ workspaceId: workspace.key, layoutId: activeLayoutQueryId.value })
        : changeWorkspaceOrLayout({ workspaceId: defaultWorkspace.value.key });
    }
  };

  /**
   * We can use only "layoutId" to determine both workspace and layout.
   * We also can use only "workspaceId" to select default layout.
   * If provided IDs are not available do nothing.
   */
  const changeWorkspaceOrLayout = (args: {
    workspaceId?: string;
    layoutId?: string;
  }) => {
    const { workspaceId = '', layoutId = '' } = args;
    if (!workspaceId && !layoutId) {
      return;
    }

    let targetLayoutId = layoutId;
    let targetWorkspaceId = workspaceId;
    let workspace = layoutWorkspaceList.value.find(w => w.key === workspaceId);

    if (workspaceId && !layoutId) {
      if (!workspace) {
        console.warn(`Workspace [${workspaceId}] is not available!`);
        return;
      }

      targetLayoutId = workspace.defaultLayout;
    }

    /* If we provide only layout ID try to find related workspace */
    if (layoutId && !workspace) {
      workspace = getWorkspaceByLayoutId(layoutId);
      if (!workspace) {
        console.warn(`Workspace with layout [${layoutId}] is not available!`);
        return;
      }

      targetWorkspaceId = workspace.key;
    }

    /* Update "isActive" value for both affected workspaces and "defaultLayout" for active workspace */
    const settings = {
      ...layout.value,
      workspaceList: layoutWorkspaceList.value.map(w => {
        const isActive = w.key === targetWorkspaceId;
        const defaultLayout = isActive ? targetLayoutId : w.defaultLayout;
        return { ...w, isActive, defaultLayout };
      }),
    };

    updateLayoutSettings(settings);
    saveUILayoutSettings({
      updatedWorkspaceIds: targetWorkspaceId !== activeWorkspaceId.value
        ? [targetWorkspaceId, activeWorkspaceId.value]
        : [targetWorkspaceId],
    });
    AppRouter.appendQuery({
      query: {
        [AppRouter.QUERY_PARAMS.WORKSPACE]: targetWorkspaceId,
        [AppRouter.QUERY_PARAMS.LAYOUT]: targetLayoutId,
      },
    });
  };

  const getWorkspaceByLayoutId = (layoutId: string) => {
    return layoutWorkspaceList.value.find(w => {
      return Object.keys(w.layouts).indexOf(layoutId) !== -1;
    });
  };

  const onActiveWorkspaceChange = (event: { key: string; }) => {
    changeWorkspaceOrLayout({ workspaceId: event.key });
  };

  const onLayoutSelect = (event: { key: string; }) => {
    changeWorkspaceOrLayout({
      workspaceId: activeWorkspaceId.value,
      layoutId: event.key,
    });
  };

  const onLayoutBookmark = (event: { key: string; }) => {
    const workspaceList = layoutWorkspaceList.value;
    const activeWorkspace = workspaceList.find(w => w.isActive)!;
    const activeLayout = activeWorkspace.layouts[event.key];

    activeWorkspace.layouts[event.key] = {
      ...activeLayout,
      bookmarkedAt: activeLayout.bookmarkedAt ? null : new Date(),
    };

    const newLayoutSettings = {
      ...layout.value,
      workspaceList: workspaceList.map(w => (w.isActive ? activeWorkspace : w)),
    };

    updateLayoutSettings(newLayoutSettings);
    saveUILayoutSettings({ updatedWorkspaceIds: [activeWorkspace.key] });
  };

  const onLayoutReset = (event: { key: string; }) => {
    const { key: layoutKey } = event;
    const currentWorkspaceList = layoutWorkspaceList.value;
    const activeWorkspace = currentWorkspaceList.find(w => w.isActive)!;
    const originalLayout = activeWorkspace.original!.layouts[layoutKey];

    activeWorkspace.layouts[layoutKey] = _.cloneDeep(originalLayout);
    const workspaceList = currentWorkspaceList.map(w => w.isActive ? activeWorkspace : w);

    updateLayoutSettings({ workspaceList });
    saveUILayoutSettings({ updatedWorkspaceIds: [activeWorkspace.key] });
  };

  const onLayoutDelete = (event: { key: string; }) => {
    const workspaceList = layoutWorkspaceList.value;
    const activeWorkspace = workspaceList.find(w => w.isActive)!;
    activeWorkspace.layouts = _.omit(activeWorkspace.layouts, event.key);
    const newLayoutSettings = {
      ...layout.value,
      workspaceList: workspaceList.map(w => (w.isActive ? activeWorkspace : w)),
    };

    updateLayoutSettings(newLayoutSettings);
    saveUILayoutSettings({ updatedWorkspaceIds: [activeWorkspace.key] });
  };

  const onLayoutCreate = (event: {
    newLayout: Layout;
    meta: { makeActive: boolean; };
  }) => {
    const { newLayout, meta } = event;
    const workspaceList = layoutWorkspaceList.value;
    const activeWorkspace = workspaceList.find(w => w.isActive)!;
    const layoutKey = newLayout.key;

    activeWorkspace.layouts = {
      ...activeWorkspace.layouts,
      [layoutKey]: newLayout,
    };

    if (meta.makeActive) {
      activeWorkspace.defaultLayout = layoutKey;
    }

    const newLayoutSettings = {
      ...layout.value,
      workspaceList: workspaceList.map(w => (w.isActive ? activeWorkspace : w)),
    };

    updateLayoutSettings(newLayoutSettings);
    saveUILayoutSettings({ updatedWorkspaceIds: [activeWorkspace.key] });
  };

  return {
    workspaceComponentsByKey,
    workspacesById,
    defaultWorkspacesById,
    activeWorkspace,
    activeWorkspaceId,
    activeLayoutId,
    initializeActiveWorkspace,
    changeWorkspaceOrLayout,
    onActiveWorkspaceChange,
    onLayoutSelect,
    onLayoutBookmark,
    onLayoutReset,
    onLayoutDelete,
    onLayoutCreate,
  };
};
